/**
 * AddToPlaylistModal Component
 * Modal for adding a movie to an existing playlist
 */

import { useEffect, useState } from 'react';
import { addMovieToPlaylist, getPlaylists, type Movie, type Playlist } from '../api';
import ModalPortal from './ModalPortal';

interface AddToPlaylistModalProps {
  movie: Movie;
  onClose: () => void;
  onAdded?: (playlist: Playlist) => void;
}

export default function AddToPlaylistModal({ movie, onClose, onAdded }: AddToPlaylistModalProps) {
  const [wantToWatchPlaylist, setWantToWatchPlaylist] = useState<Playlist | null>(null);
  const [regularPlaylists, setRegularPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    async function loadPlaylists() {
      try {
        setLoading(true);
        const data = await getPlaylists();
        // Only show owned playlists that are completed (can add movies to)
        // Also include shared playlists with edit permission
        // Exclude 'watched' playlist (movies can't be manually added to it)
        const availablePlaylists = [
          ...data.playlists.filter((p) => p.status === 'completed' && p.type !== 'watched'),
          ...data.shared_playlists.filter(
            (p) => p.status === 'completed' && p.permission === 'edit'
          ),
        ];

        // Separate Want to Watch from regular playlists
        const wtw = availablePlaylists.find((p) => p.type === 'want_to_watch') || null;
        const regular = availablePlaylists.filter((p) => p.type !== 'want_to_watch');

        setWantToWatchPlaylist(wtw);
        setRegularPlaylists(regular);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load playlists');
      } finally {
        setLoading(false);
      }
    }

    loadPlaylists();
  }, []);

  const handleAddToPlaylist = async (playlist: Playlist) => {
    setAdding(playlist.id);
    setError(null);

    try {
      await addMovieToPlaylist(playlist.id, movie.tmdb_id);
      setSuccessMessage(`Added to "${playlist.name}"`);
      onAdded?.(playlist);

      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add movie';
      // Check if it's a duplicate error
      if (message.toLowerCase().includes('already')) {
        setError(`"${movie.title}" is already in "${playlist.name}"`);
      } else {
        setError(message);
      }
      setAdding(null);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalPortal>
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div className="modal add-to-playlist-modal">
          <div className="modal-header">
            <h2>Add to Playlist</h2>
            <button className="modal-close" onClick={onClose} aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="modal-body">
            {/* Movie being added */}
            <div className="add-to-playlist-movie">
              {movie.poster_url && (
                <img src={movie.poster_url} alt={movie.title} className="add-to-playlist-poster" />
              )}
              <div className="add-to-playlist-movie-info">
                <span className="add-to-playlist-movie-title">
                  {movie.original_title || movie.title}
                </span>
                {movie.year && <span className="add-to-playlist-movie-year">{movie.year}</span>}
              </div>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="add-to-playlist-success">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                {successMessage}
              </div>
            )}

            {/* Error */}
            {error && <div className="modal-error">{error}</div>}

            {/* Loading */}
            {loading && (
              <div className="add-to-playlist-loading">
                <div className="spinner" />
                <span>Loading playlists...</span>
              </div>
            )}

            {/* Playlist List */}
            {!loading && !successMessage && (
              <>
                {!wantToWatchPlaylist && regularPlaylists.length === 0 ? (
                  <div className="add-to-playlist-empty">
                    <p>No playlists available.</p>
                    <p className="add-to-playlist-hint">
                      Create a playlist first from the home page.
                    </p>
                  </div>
                ) : (
                  <div className="add-to-playlist-list">
                    {/* Want to Watch at the top */}
                    {wantToWatchPlaylist && (
                      <>
                        <button
                          className="add-to-playlist-item add-to-playlist-item-wtw"
                          onClick={() => handleAddToPlaylist(wantToWatchPlaylist)}
                          disabled={adding !== null}
                        >
                          <div className="add-to-playlist-item-info">
                            <span className="add-to-playlist-item-name">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                width="16"
                                height="16"
                                style={{ marginRight: '6px', verticalAlign: 'middle' }}
                              >
                                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                              </svg>
                              {wantToWatchPlaylist.name}
                            </span>
                            <span className="add-to-playlist-item-count">
                              {wantToWatchPlaylist.movie_count || 0} movies
                            </span>
                          </div>
                          {adding === wantToWatchPlaylist.id ? (
                            <span className="action-spinner" />
                          ) : (
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              className="add-to-playlist-item-icon"
                            >
                              <line x1="12" y1="5" x2="12" y2="19" />
                              <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                          )}
                        </button>
                        {regularPlaylists.length > 0 && <div className="add-to-playlist-divider" />}
                      </>
                    )}

                    {/* Regular playlists */}
                    {regularPlaylists.map((playlist) => (
                      <button
                        key={playlist.id}
                        className="add-to-playlist-item"
                        onClick={() => handleAddToPlaylist(playlist)}
                        disabled={adding !== null}
                      >
                        <div className="add-to-playlist-item-info">
                          <span className="add-to-playlist-item-name">{playlist.name}</span>
                          <span className="add-to-playlist-item-count">
                            {playlist.movie_count || 0} movies
                            {playlist.is_shared && ' • Shared'}
                          </span>
                        </div>
                        {adding === playlist.id ? (
                          <span className="action-spinner" />
                        ) : (
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className="add-to-playlist-item-icon"
                          >
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
